import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rolesApi, usersApi } from '@/api/usersApi';
import type {
  AssignEnterpriseRequest,
  CreateUserRequest,
  ResetPasswordRequest,
  UpdateUserRequest,
  UserListRequest,
} from '@/types/user';

const KEYS = {
  all: ['users'] as const,
  list: (f: UserListRequest) => [...KEYS.all, 'list', f] as const,
  detail: (id: number) => [...KEYS.all, 'detail', id] as const,
};

export function useUsers(filters: UserListRequest = {}) {
  return useQuery({ queryKey: KEYS.list(filters), queryFn: () => usersApi.list(filters) });
}

export function useUser(id: number | null) {
  return useQuery({
    queryKey: KEYS.detail(id ?? 0),
    queryFn: () => usersApi.getById(id!),
    enabled: id != null,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: CreateUserRequest) => usersApi.create(r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateUserRequest }) =>
      usersApi.update(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => usersApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => usersApi.reactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => usersApi.unlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: ResetPasswordRequest }) =>
      usersApi.resetPassword(id, request),
  });
}

export function useAssignEnterprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: AssignEnterpriseRequest }) =>
      usersApi.assignEnterprise(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useRemoveEnterprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enterpriseId }: { id: number; enterpriseId: number }) =>
      usersApi.removeEnterprise(id, enterpriseId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useAssignMdm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, mdmId }: { userId: number; mdmId: number }) =>
      usersApi.assignMdm(userId, mdmId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useRemoveMdm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, mdmId }: { userId: number; mdmId: number }) =>
      usersApi.removeMdm(userId, mdmId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
    staleTime: 5 * 60 * 1000, // 5 min - los roles cambian poco
  });
}
